// Date-range filtering for operator list views.
//
// `<input type="date">` yields a bare "YYYY-MM-DD" with no timezone. Payout
// created_at is an ISO-8601 UTC instant. Turning one into the other is the
// whole job here, and it is where the obvious implementations go wrong:
//
//   new Date("2026-06-09")            → midnight UTC, NOT midnight local.
//   created_at.startsWith(from)       → string prefix match, so a payout at
//                                       23:30 IST on 9 Jun (18:00Z 9 Jun) and
//                                       one at 00:30 IST on 10 Jun (19:00Z
//                                       9 Jun) both claim to be "9 Jun".
//
// Both make the filter disagree with the timestamps rendered next to it,
// which for a money view is worse than having no filter: an operator
// reconciling a day's outflow would be shown a total that silently excludes
// rows they can see on screen.
//
// So the range is resolved in the VIEWER'S LOCAL timezone, the same basis
// formatDateTime / formatDay print in. "9 Jun to 9 Jun" means local
// 09 Jun 00:00:00.000 through 09 Jun 23:59:59.999 — INCLUSIVE at both ends,
// because an operator who types the same day twice means "that day", not
// "an empty window".

/**
 * ymdLocal renders a Date as the "YYYY-MM-DD" an `<input type="date">` wants,
 * in LOCAL terms.
 *
 * Not toISOString().slice(0, 10) — that is UTC, so for anyone east of
 * Greenwich a "Today" preset clicked before 05:30 IST would fill in
 * yesterday's date and hide the very payouts the operator opened the page to
 * see.
 */
export function ymdLocal(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * daysAgoLocal returns the "YYYY-MM-DD" n days back, counting today as day 1
 * — so `daysAgoLocal(7)` is the start of a 7-day window ENDING today, which
 * is what "last 7 days" means to an operator. Off-by-one here shows an 8-day
 * total under a 7-day label.
 */
export function daysAgoLocal(n: number, now: Date = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  d.setDate(d.getDate() - (n - 1));
  return ymdLocal(d);
}

/**
 * yesterdayLocal / startOfWeekLocal / startOfMonthLocal — the other presets
 * an operator reaches for. "This week" starts on Monday (the working week
 * the payouts are reconciled against, not the US calendar's Sunday).
 */
export function yesterdayLocal(now: Date = new Date()): string {
  return daysAgoLocal(2, now);
}

export function startOfWeekLocal(now: Date = new Date()): string {
  const d = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  // getDay(): 0 = Sunday … 6 = Saturday. Days since Monday, with Sunday
  // counting as the 7th day of the week that began the previous Monday.
  const sinceMonday = (d.getDay() + 6) % 7;
  d.setDate(d.getDate() - sinceMonday);
  return ymdLocal(d);
}

export function startOfMonthLocal(now: Date = new Date()): string {
  return ymdLocal(new Date(now.getFullYear(), now.getMonth(), 1));
}

export interface DateRangePreset {
  value: string;
  label: string;
  from: string;
  to: string;
}

/**
 * The two preset sets in use (rendered by <DateRangeFilter>). A list view asks "what went out recently" —
 * all time / today / 7 / 30 days. A dashboard asks "how did the period go"
 * — today / yesterday / this week / this month, the periods an operator
 * reports on. Both are built from the same clock so a preset's dates never
 * disagree with the inputs next to it.
 */
export function listPresets(now: Date = new Date()): DateRangePreset[] {
  const today = ymdLocal(now);
  return [
    { value: "all", label: "All time", from: "", to: "" },
    { value: "today", label: "Today", from: today, to: today },
    { value: "7d", label: "7 days", from: daysAgoLocal(7, now), to: today },
    { value: "30d", label: "30 days", from: daysAgoLocal(30, now), to: today },
  ];
}

export function periodPresets(now: Date = new Date()): DateRangePreset[] {
  const today = ymdLocal(now);
  const yesterday = yesterdayLocal(now);
  return [
    { value: "today", label: "Today", from: today, to: today },
    { value: "yesterday", label: "Yesterday", from: yesterday, to: yesterday },
    { value: "week", label: "This week", from: startOfWeekLocal(now), to: today },
    { value: "month", label: "This month", from: startOfMonthLocal(now), to: today },
  ];
}

/** Local start-of-day for a "YYYY-MM-DD" string, or null if unparseable. */
function startOfLocalDay(ymd: string): Date | null {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return null;
  const [, y, mo, d] = m;
  // Explicit component constructor — this is the local-midnight form.
  // Date.parse("YYYY-MM-DD") would give UTC midnight instead.
  const dt = new Date(Number(y), Number(mo) - 1, Number(d));
  return Number.isNaN(dt.getTime()) ? null : dt;
}

/**
 * inDateRange reports whether an ISO timestamp falls inside [from, to],
 * both bounds given as "YYYY-MM-DD" and both inclusive.
 *
 * An empty or malformed bound is treated as UNBOUNDED on that side, so a
 * half-typed date in the input box degrades to "no filter yet" rather than
 * blanking the table mid-keystroke. Both empty → everything passes.
 */
export function inDateRange(
  iso: string,
  from: string,
  to: string,
): boolean {
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return true; // unparseable row timestamp: don't hide it

  const start = from.trim() === "" ? null : startOfLocalDay(from);
  if (start && t < start.getTime()) return false;

  const endDay = to.trim() === "" ? null : startOfLocalDay(to);
  if (endDay) {
    // Inclusive end: advance to the START of the next local day and use a
    // strict <. Adding 86_400_000 - 1 ms instead would be wrong across a
    // DST transition, where a local day is 23 or 25 hours long.
    const end = new Date(
      endDay.getFullYear(),
      endDay.getMonth(),
      endDay.getDate() + 1,
    );
    if (t >= end.getTime()) return false;
  }
  return true;
}

/**
 * DateRangeBounds is a [from, to) window as ISO instants, for the backend
 * aggregate endpoints (?from=/?to=). Either side undefined = unbounded.
 */
export interface DateRangeBounds {
  from?: string;
  to?: string;
}

/**
 * dateRangeBounds turns the same "YYYY-MM-DD" pair inDateRange filters rows
 * with into the instants the server needs to count the same rows — local
 * start of the from-day, and the start of the day AFTER the to-day so the
 * end is inclusive without an off-by-a-millisecond (or, across DST, an
 * off-by-an-hour). The two functions MUST agree: a dashboard tile that says
 * 12 must link to a list that shows 12.
 *
 * Empty or malformed bounds are unbounded, matching inDateRange.
 */
export function dateRangeBounds(from: string, to: string): DateRangeBounds {
  const out: DateRangeBounds = {};
  const start = from.trim() === "" ? null : startOfLocalDay(from);
  if (start) out.from = start.toISOString();
  const endDay = to.trim() === "" ? null : startOfLocalDay(to);
  if (endDay) {
    out.to = new Date(
      endDay.getFullYear(),
      endDay.getMonth(),
      endDay.getDate() + 1,
    ).toISOString();
  }
  return out;
}

/**
 * isReversedRange reports a from-date later than the to-date, so the caller
 * can say so instead of rendering a legitimately-empty table that looks
 * identical to "this merchant has no payouts".
 */
export function isReversedRange(from: string, to: string): boolean {
  const a = from.trim() === "" ? null : startOfLocalDay(from);
  const b = to.trim() === "" ? null : startOfLocalDay(to);
  if (!a || !b) return false;
  return a.getTime() > b.getTime();
}
